/**
 * Text Undo/Redo Utilities
 * Following the same pattern as undoRedoLogo.js but adapted for text functionality
 */
import { redoText, scene, undoText, mesh } from '../webGL';
import { textsOfHandling } from '../textHandling';

// Text undo/redo stacks (similar to undoLogo/redoLogo)
const MAX_UNDO_STACK_SIZE = 50;

export function pushTextUndoQueue() {
  // Before resetting redoText, restore all actions from redoText back to undoText
  // This ensures that when user adds a new text, the previously undone actions
  // are still available in undo history
  if (redoText && redoText.length > 0) {
    // Push all actions from redoText back to undoText to preserve undo history
    // The order in redoText is: [newest undone, older undone, ...]
    // We need to push them in reverse order to maintain correct timeline in undoText
    for (let i = redoText.length - 1; i >= 0; i--) {
      undoText.push(redoText[i]);
    }
    // Now clear redoText since we've restored all actions to undoText
    redoText.splice(0);
  }

  if (!window.decals || window.decals.length === 0) return;

  const textAction = [];
  window.decals.forEach((x) => {
    // Only include text objects, exclude logo objects/pattern objects
    if (x && x.logo && x.logo.IsText === true && x.logo.IsPattern !== true) {
      textAction.push(convertTextData(x));
    }
  });
  // Only push if there are text objects to save
  if (textAction.length > 0) {
    undoText.push(textAction);
    if (undoText.length > MAX_UNDO_STACK_SIZE) {
      undoText.shift();
    }
  }
}

function convertTextData(value) {
  const logo = value?.logo;
  const Position = logo?.Position
    ? Object.assign(Object.create(Object.getPrototypeOf(logo.Position)), logo.Position)
    : null;
  const Orientation = logo?.Orientation
    ? Object.assign(Object.create(Object.getPrototypeOf(logo.Orientation)), logo.Orientation)
    : null;

  return {
    type: 'text',
    value: value,
    logo: logo,
    Position,
    Orientation,
    text: logo?.Text,
    color: logo?.Color,
    font: logo?.Font,
    rotate: logo?.Rotate,
    scale: logo?.Scale,
    meshId: logo?.meshID,
    isText: logo?.IsText,
    timestamp: Date.now(),
  };
}

export function doUndoText() {
  if (undoText.length === 0) return;
  if (undoText.length === 1) {
    const lastAction = undoText.pop();
    redoText.push(lastAction);
    // Remove all text objects from scene and window.decals
    window.decals.forEach((d) => {
      if (d.logo && d.logo.IsText === true) {
        try {
          scene.remove(d);
          if (d.geometry) {
            d.geometry.dispose();
          }
          if (d.material) {
            if (d.material.map) {
              d.material.map.dispose();
            }
            d.material.dispose();
          }
        } catch (error) {}
      }
    });
    // Update window.decals to remove only text objects, keep logo objects
    window.decals = window.decals.filter((d) => !d.logo || d.logo.IsText !== true);
    // Sync textsOfHandling.texts
    if (textsOfHandling && textsOfHandling.texts) {
      textsOfHandling.texts.splice(0);
    }
    if (window.clearTextUIState) {
      try {
        window.clearTextUIState();
      } catch (e) {}
    }
    return;
  }
  const lastAction = undoText.pop();
  redoText.push(lastAction);
  if (redoText.length > MAX_UNDO_STACK_SIZE) {
    redoText.shift();
  }
  const current = undoText[undoText.length - 1];
  syncTheTexts(current);
}

function syncTheTexts(currentAction) {
  const _currentAction =
    typeof currentAction === 'object' && Array.isArray(currentAction) && currentAction.length > 0
      ? currentAction
      : currentAction
      ? [currentAction]
      : [];
  const keptIds = new Set(_currentAction.map((td) => td && td.logo && td.logo.Id).filter(Boolean));

  // Only remove text objects from scene and window.decals, keep logo objects
  window.decals.forEach((d) => {
    if (d.logo && d.logo.IsText === true) {
      const id = d.logo && d.logo.Id;
      if (!id || !keptIds.has(id)) {
        try {
          scene.remove(d);
          if (d.geometry) {
            d.geometry.dispose();
          }
          if (d.material) {
            if (d.material.map) {
              d.material.map.dispose();
            }
            d.material.dispose();
          }
        } catch (error) {}
      }
    }
  });
  // Update window.decals to remove only text objects that are not in keptIds
  window.decals = window.decals.filter(
    (d) => !d.logo || d.logo.IsText !== true || (d.logo.Id && keptIds.has(d.logo.Id)),
  );

  if (!_currentAction || _currentAction.length === 0) {
    if (window.clearTextUIState) {
      try {
        window.clearTextUIState();
      } catch (e) {}
    }
    return;
  }

  // Restore text objects from undo queue
  _currentAction.forEach((textData) => {
    if (textData && textData.logo && textData.logo.IsText === true) {
      const logoId = textData.logo.Id;
      if (!logoId) return;

      // First, remove all existing decalMesh with the same Id from scene and dispose them
      window.decals.forEach((d) => {
        if (d && d.logo && d.logo.Id === logoId) {
          try {
            scene.remove(d);
            if (d.geometry) {
              d.geometry.dispose();
            }
            if (d.material) {
              if (d.material.map) {
                d.material.map.dispose();
              }
              d.material.dispose();
            }
          } catch (error) {}
        }
      });

      // Remove from window.decals
      window.decals = window.decals.filter((d) => !d.logo || d.logo.Id !== logoId);

      // Update logo properties
      if (textData.rotate !== undefined) textData.logo.Rotate = textData.rotate;
      if (textData.scale !== undefined) textData.logo.Scale = textData.scale;
      if (textData.text !== undefined) textData.logo.Text = textData.text;
      if (textData.color !== undefined) textData.logo.Color = textData.color;
      if (textData.font !== undefined) textData.logo.Font = textData.font;
      if (textData.meshId !== undefined) textData.logo.meshID = textData.meshId;
      const THREERef = typeof window !== 'undefined' && window.THREE ? window.THREE : null;
      if (!THREERef) return;

      if (textData.Position) {
        textData.logo.Position = textData.Position.clone
          ? textData.Position.clone()
          : new THREERef.Vector3(textData.Position.x, textData.Position.y, textData.Position.z);
      }
      if (textData.Orientation) {
        textData.logo.Orientation = textData.Orientation.copy
          ? textData.Orientation.clone()
          : new THREERef.Euler(
              textData.Orientation._x || textData.Orientation.x,
              textData.Orientation._y || textData.Orientation.y,
              textData.Orientation._z || textData.Orientation.z,
              textData.Orientation._order || textData.Orientation.order,
            );
      }

      // Re-render the texture if text content exists
      if (
        window.renderTextOnly &&
        textData.logo.Text &&
        textData.logo.Color &&
        textData.logo.Font
      ) {
        try {
          window.renderTextOnly(textData.logo);
        } catch (error) {}
      }

      // Create new decalMesh with restored position/orientation
      if (
        textData.Position &&
        textData.Orientation &&
        textData.logo.decalMaterial &&
        scene &&
        THREERef
      ) {
        try {
          // Find target mesh
          let targetMesh = null;
          if (textData.meshId !== undefined && textData.meshId !== null) {
            if (Array.isArray(mesh) && mesh[textData.meshId]) {
              targetMesh = mesh[textData.meshId];
            } else if (scene && scene.traverse) {
              scene.traverse((child) => {
                if (child && child.meshId === textData.meshId) {
                  targetMesh = child;
                }
              });
            }
          }

          if (targetMesh) {
            // Create orientation with rotation applied
            const orientationTemp = new THREERef.Euler();
            orientationTemp.copy(textData.Orientation);
            orientationTemp.z += ((textData.rotate || 0) * Math.PI) / 180;

            // Set scale
            const scaleValue = textData.scale || 1;
            const sizeVector = new THREERef.Vector3(scaleValue, scaleValue, scaleValue);

            // Create new decal mesh
            const newDecalMesh = new THREERef.Mesh(
              new THREERef.DecalGeometry(
                targetMesh,
                textData.Position,
                orientationTemp,
                sizeVector,
              ),
              textData.logo.decalMaterial,
            );

            newDecalMesh.logo = textData.logo;
            newDecalMesh.renderOrder = 1;
            textData.logo.decalMesh = newDecalMesh;

            scene.add(newDecalMesh);
            window.decals.push(newDecalMesh);
          }
        } catch (e) {
          console.error('Error restoring text decalMesh:', e);
        }
      }
    }
  });

  // Sync textsOfHandling.texts with window.decals (text objects only)
  if (textsOfHandling && textsOfHandling.texts) {
    textsOfHandling.texts.splice(0);
    window.decals.forEach((d) => {
      if (d && d.logo && d.logo.IsText === true) {
        textsOfHandling.texts.push(d);
      }
    });
  }

  if (window.currentLogo) window.currentLogo = null;

  if (_currentAction.length > 0 && window.updateTextUIState) {
    const lastText = _currentAction[_currentAction.length - 1];
    if (lastText && lastText.logo) window.updateTextUIState(lastText.logo);
  }
}

export function doRedoText() {
  if (redoText?.length > 0) {
    const lastAction = redoText.pop();
    undoText.push(lastAction);
    if (undoText.length > MAX_UNDO_STACK_SIZE) {
      undoText.shift();
    }
    syncTheTexts(lastAction);
  }
}
